/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { AsyncPipe, NgClass } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { NavigationEnd, Router, RouterLink } from "@angular/router";
import { filter, map, Observable, of, startWith } from "rxjs";
import { Role } from "../../models/ui/role.model";
import { SidebarItem } from "../../models/ui/sidebar-item.model";
import { AuthorizationService } from "../../services/authorization.service";
import { StorageService } from "../../services/storage.service";
import { SIDEBAR_ITEMS } from "../../utils/constants";
import { ButtonComponent } from "../button/button.component";

@Component({
	selector: "globalictcc-sidebar",
	standalone: true,
	imports: [RouterLink, NgClass, ButtonComponent, AsyncPipe],
	templateUrl: "./sidebar.component.html",
	styleUrl: "./sidebar.component.scss"
})
export class SidebarComponent implements OnInit {
	public isSidebarCollapsed = false;
	public currentRoute: string = "";
	public sidebarItems$: Observable<SidebarItem[]> = of(SIDEBAR_ITEMS);

	constructor(
		private _router: Router,
		private _storageService: StorageService,
		private _authorizationService: AuthorizationService
	) {}

	ngOnInit() {
		this.sidebarItems$ = this._router.events.pipe(
			startWith(new NavigationEnd(0, this._router.url, this._router.url)),
			filter(event => event instanceof NavigationEnd),
			map(() =>
				SIDEBAR_ITEMS.filter(item => this._isSidebarItemVisible(item)).map(item => ({
					...item,
					isActive: this._router.url.includes(item.routerLink)
				}))
			)
		);
	}

	/**
	 * Determines whether a sidebar item should be visible based on the user's role and grants.
	 *
	 * @param item - The sidebar item to check visibility for.
	 * @returns `true` if the user matches the item's roles (if any) and has its grant (if any); otherwise, `false`.
	 */
	private _isSidebarItemVisible(item: SidebarItem): boolean {
		const userRole = this._storageService.getUserRole() as Role;

		return (
			(!item.roles || item.roles.includes(userRole)) &&
			(!item.grant || this._authorizationService.hasGrant(item.grant))
		);
	}

	/**
	 * Toggles the state of the sidebar between collapsed and expanded.
	 * When called, it inverses the current state of `isSidebarCollapsed`.
	 */
	public toggleSidebar() {
		this.isSidebarCollapsed = !this.isSidebarCollapsed;
	}
}
